import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import {
  Filter,
  Search,
  Paperclip,
  Send,
  MessageSquare,
  Trash2,
  Mic,
  StopCircle,
  Calendar,
  ArchiveRestore,
  MessageCirclePlus,
  Pencil,
  LoaderCircle,
} from "lucide-react";

import { useWebSocket } from "../context/WebSocketContext";
import { useAuth } from "../context/AuthContext";

import ScheduledMessagesList from "../components/conversations/ScheduleMessagesList";
import FiltersList from "../components/conversations/FiltersList";
import AttachmentList from "../components/conversations/AttachmentList";
import ClientSidebar from "../components/conversations/ClientSidebar";
import ConversationsList from "../components/conversations/ConversationsList";
import SearchResults from "../components/conversations/SearchResults";
import ChatHeader from "../components/conversations/chat/ChatHeader";
import ChatMessages from "../components/conversations/chat/ChatMessages";
import QuickMessageBlock from "../components/conversations/QuickMessageBlock";
import Dropzone from "../components/Dropzone";
import EmotesPanel from "../components/conversations/EmotesPanel";
import DirectMessageList from "../modal/DirectMessageModal";
import ForwardMessageModal from "../modal/ForwardMessageModal";

import { useConversationsApi } from "../api/conversationsApi";

import { useContacts } from "../hooks/useContacts";
import { useUsers } from "../hooks/useUsers";
import { useTags } from "../hooks/useTags";
import { useFunnels } from "../hooks/useFunnels";
import { useConversations } from "../hooks/useConversations";
import { useAudioRecorder } from "../hooks/useAudioRecorder";
import { useQuickMessages } from "../hooks/useQuickMessages";
import { useAttachments } from "../hooks/useAttachments";
import { usePins } from "../hooks/usePins";
import { useDebounce } from "../hooks/useDebounce";
import { useConversationSearch } from "../hooks/useConversationSearch";

import { inputButtons } from "../config/inputButtons";
import { getFileUrl, getFileType } from "../utils/helpers";
import { normalizeText } from "../utils/messageHelpers";
import { notify } from "../utils/notification";

const Conversations = ({ focusConversationId = null, onFocusHandled }) => {
  const { sendAction, messages } = useWebSocket();
  const { user } = useAuth();

  const api = useConversationsApi();
  const { contacts } = useContacts();
  const { users } = useUsers();
  const { tags } = useTags();
  const { funnels } = useFunnels();
  const { quickMessages } = useQuickMessages();
  const { attachments, addAttachment } = useAttachments();
  const {
    conversations,
    setConversations,
    summarizeConversation,
    deleteConversation,
    loading,
    error,
    fetchConversations,
    fetchFilteredConversations,
    fetchMoreConversations,
  } = useConversations();
  const { pinnedIds, togglePinConversation } = usePins(user.id);
  const {
    isRecording,
    recordingTime,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    cancelRecording,
    formatRecordingTime,
  } = useAudioRecorder();

  const [selectedChat, setSelectedChat] = useState(null);
  const [activePanel, setActivePanel] = useState(null);

  const [showContactInfo, setShowContactInfo] = useState(false);
  const [openDirectMessageModal, setOpenDirectMessageModal] = useState(false);
  const [openForwardMessageModal, setOpenForwardMessageModal] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    name: "",
    phone: "",
    status: "",
    unread: false,
    users: [],
    tags: [],
    steps: [],
  });

  const [sendingMessage, setSendingMessage] = useState(false);
  const [payload, setPayload] = useState({
    text: "",
    quoteMessageId: null,
    attachment: null,
    scheduleAt: "",
  });

  const messageInputRef = useRef(null);
  const lastNotifiedMessageId = useRef(null);
  const lastScheduledToastRef = useRef(null);
  const loadedOlderRef = useRef(new Set());
  const connectionEstablishedRef = useRef(false);
  const lastProcessedEventIdRef = useRef(null);

  const handleMessageSchedulingUpdate = useCallback(
    (scheduledMsg) => {
      const conversationId = scheduledMsg.conversationId;

      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id !== conversationId) return conv;

          const index = conv.scheduledMessages?.findIndex(
            (m) => m.id === scheduledMsg.id,
          );

          if (index !== -1) {
            const updated = [...conv.scheduledMessages];
            updated[index] = scheduledMsg;
            return { ...conv, scheduledMessages: updated };
          }

          return {
            ...conv,
            scheduledMessages: [
              ...(conv.scheduledMessages || []),
              scheduledMsg,
            ],
          };
        }),
      );

      setSelectedChat((prev) => {
        if (!prev || prev.id !== conversationId) return prev;

        const index = prev.scheduledMessages?.findIndex(
          (m) => m.id === scheduledMsg.id,
        );

        if (index !== -1) {
          const updated = [...prev.scheduledMessages];
          updated[index] = scheduledMsg;
          return { ...prev, scheduledMessages: updated };
        }

        return {
          ...prev,
          scheduledMessages: [...(prev.scheduledMessages || []), scheduledMsg],
        };
      });
    },
    [setConversations, setSelectedChat],
  );

  const handleTranscriptUpdate = useCallback(
    ({ msgId, transcription }) => {
      setConversations((prev) =>
        prev.map((conv) => ({
          ...conv,
          messages: conv.messages?.map((msg) =>
            msg.id === msgId ? { ...msg, messageBody: transcription } : msg,
          ),
        })),
      );

      setSelectedChat((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.id === msgId ? { ...msg, messageBody: transcription } : msg,
          ),
        };
      });
    },
    [setConversations],
  );

  const debouncedFilters = useDebounce(filters, 500);
  const debouncedSearch = useDebounce(filters.search, 400);
  const searching = debouncedSearch.trim().length >= 2;
  const { result: searchResult, loading: searchLoading } = useConversationSearch(
    debouncedSearch,
    { enabled: searching },
  );

  // Abre um resultado da busca. A conversa pode não estar na lista carregada
  // (busca alcança o histórico inteiro), então buscamos por id quando falta.
  const openFromSearch = useCallback(
    async (conversationId) => {
      const loaded = conversations.find((item) => item.id === conversationId);
      if (loaded) {
        setSelectedChat(loaded);
        return;
      }

      try {
        const conversation = await api.getById(conversationId);
        if (!conversation) return;

        setConversations((prev) =>
          prev.some((item) => item.id === conversation.id)
            ? prev
            : [conversation, ...prev],
        );
        setSelectedChat(conversation);
      } catch (err) {
        console.error("Erro ao abrir resultado da busca:", err);
        toast.error("Não foi possível abrir a conversa.");
      }
    },
    [api, conversations, setConversations],
  );

  const updateFilters = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const cancelScheduledMessage = (jobId) => {
    if (!jobId) return;
    sendAction("cancel_scheduled_message", { jobId });
  };

  const handleIncomingNotification = useCallback(
    (msg) => {
      if (!msg) return;
      if (msg.direction !== "INCOMING") return;

      if (lastNotifiedMessageId.current === msg.messageId) return;
      lastNotifiedMessageId.current = msg.messageId;

      const isOpen =
        selectedChat?.id === msg.conversationId && !document.hidden;
      const isTabHidden = document.visibilityState !== "visible";

      if (isOpen || !isTabHidden) return;

      const conversation = conversations.find(
        (c) => c.id === msg.conversationId,
      );

      if (!conversation) return;

      notify({
        title: `Nova mensagem de ${conversation.contact.name}`,
        body:
          msg.messageType === "text"
            ? msg.messageBody.slice(0, 80)
            : "Nova mensagem",
        onClick: () => {
          setSelectedChat(conversation);
        },
      });
    },
    [selectedChat, conversations],
  );

  // `search` saiu daqui: o texto livre agora vai para o endpoint de busca, que
  // usa índice. Deixá-lo aqui manteria a consulta com ILIKE varrendo mensagens.
  const hasActiveFilters = (filters) => {
    return (
      filters.name.trim() !== "" ||
      filters.phone.trim() !== "" ||
      filters.status !== "" ||
      filters.unread ||
      filters.users.length > 0 ||
      filters.tags.length > 0 ||
      filters.steps.length > 0
    );
  };

  // Abre a conversa pedida por outra tela. Se ela não estiver na lista carregada
  // (fora do período), busca sozinha em vez de deixar o gestor procurar.
  useEffect(() => {
    if (!focusConversationId || loading) return;

    const alreadyLoaded = conversations.find(
      (conversation) => conversation.id === focusConversationId,
    );

    if (alreadyLoaded) {
      setSelectedChat(alreadyLoaded);
      onFocusHandled?.();
      return;
    }

    let cancelled = false;

    api
      .getById(focusConversationId)
      .then((conversation) => {
        if (cancelled || !conversation) return;
        setConversations((prev) =>
          prev.some((item) => item.id === conversation.id)
            ? prev
            : [conversation, ...prev],
        );
        setSelectedChat(conversation);
      })
      .catch((err) => {
        console.error("Erro ao abrir conversa:", err);
        if (!cancelled) toast.error("Não foi possível abrir a conversa.");
      })
      .finally(() => {
        if (!cancelled) onFocusHandled?.();
      });

    return () => {
      cancelled = true;
    };
  }, [
    api,
    conversations,
    focusConversationId,
    loading,
    onFocusHandled,
    setConversations,
  ]);

  useEffect(() => {
    setPayload({
      text: "",
      attachment: null,
      quoteMessageId: null,
      scheduleAt: "",
    });
  }, [selectedChat?.id]);

  useEffect(() => {
    if (selectedChat && messageInputRef.current) {
      setTimeout(() => {
        messageInputRef.current.focus();
      }, 0);
    }
  }, [selectedChat]);

  useEffect(() => {
    if (!messages.length) return;

    const last = messages[0];
    if (last.type !== "loaded_older_messages") return;
    if (!selectedChat) return;

    if (last.conversationId !== selectedChat.id) return;

    const batchKey =
      last.conversationId + "-" + (last.result?.[0]?.messageId || "empty");

    if (loadedOlderRef.current.has(batchKey)) return;
    loadedOlderRef.current.add(batchKey);

    const olderMessages = last.result || [];
    const mergeMessages = (current = []) => {
      const byId = new Map();

      for (const message of [...olderMessages, ...current]) {
        byId.set(message.messageId || message.id, message);
      }

      return [...byId.values()].sort(
        (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
      );
    };

    setSelectedChat((prev) => ({
      ...prev,
      messages: mergeMessages(prev.messages),
    }));

    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.id !== last.conversationId) return conv;

        return {
          ...conv,
          messages: mergeMessages(conv.messages),
        };
      }),
    );
  }, [messages, selectedChat, setConversations]);

  useEffect(() => {
    if (!messages.length) return;

    const lastMessage = messages[0];
    if (lastProcessedEventIdRef.current === lastMessage.id) return;
    lastProcessedEventIdRef.current = lastMessage.id;

    switch (lastMessage.type) {
      case "connection_established": {
        if (!connectionEstablishedRef.current) {
          connectionEstablishedRef.current = true;
          return;
        }

        const cursorDate = new Date();
        cursorDate.setHours(0, 0, 0, 0);
        cursorDate.setDate(cursorDate.getDate() + 1);
        fetchConversations(cursorDate).then((freshConversations) => {
          if (!freshConversations) return;

          setSelectedChat((current) =>
            current
              ? freshConversations.find(
                  (conversation) => conversation.id === current.id,
                ) || current
              : current,
          );
        });
        return;
      }

      case "scheduled_message_added":
        handleMessageSchedulingUpdate(lastMessage.result);

        if (lastScheduledToastRef.current !== lastMessage.result?.id) {
          lastScheduledToastRef.current = lastMessage.result?.id;
          toast.success("Mensagem agendada com sucesso!");
        }
        return;

      case "scheduled_message_status":
        handleMessageSchedulingUpdate(lastMessage.result);
        return;

      case "transcripted_audio":
        handleTranscriptUpdate(lastMessage.result);
        return;

      case "whatsapp_message_read": {
        const readUpdate = lastMessage.result;
        if (!readUpdate?.conversationId) return;

        const applyReadUpdate = (conversation) => {
          if (conversation.id !== readUpdate.conversationId) {
            return conversation;
          }

          return {
            ...conversation,
            messages: (conversation.messages || []).map((message) =>
              message.direction === readUpdate.direction
                ? { ...message, isRead: true }
                : message,
            ),
          };
        };

        setConversations((prev) => prev.map(applyReadUpdate));
        setSelectedChat((prev) => (prev ? applyReadUpdate(prev) : prev));
        return;
      }

      case "whatsapp_message_sent":
      case "whatsapp_message_received": {
        if (!lastMessage.result) return;
        const { messageCount, isNewConversation } = lastMessage.result;

        if (isNewConversation) {
          const newConv = lastMessage.result;
          const initiatedByCurrentCrm =
            lastMessage.type === "whatsapp_message_sent" &&
            lastMessage.source === "crm_send" &&
            String(lastMessage.initiatedByUserId) === String(user?.id);

          setConversations((prev) => [
            newConv,
            ...prev.filter((conversation) => conversation.id !== newConv.id),
          ]);

          setSelectedChat((prev) =>
            initiatedByCurrentCrm || prev?.id === newConv.id ? newConv : prev,
          );

          if (initiatedByCurrentCrm) {
            setOpenDirectMessageModal(false);
          }

          const lastMsg = newConv.messages?.[newConv.messages.length - 1];

          handleIncomingNotification(lastMsg);

          return;
        } else {
          const { msg, unreadCount, lastMessageAt } = lastMessage.result;
          if (!msg) return;

          setConversations((prev) => {
            const next = [];
            let updatedConv = null;

            for (const conv of prev) {
              if (conv.id === msg.conversationId) {
                updatedConv = {
                  ...conv,
                  messageCount: Math.max(conv.messageCount, messageCount),
                  lastMessageAt,
                  _count: {
                    ...conv._count,
                    messages: unreadCount,
                  },
                  messages: conv.messages.some(
                    (message) =>
                      message.messageId === msg.messageId ||
                      (msg.providerMessageKey &&
                        message.providerMessageKey === msg.providerMessageKey),
                  )
                    ? conv.messages
                    : [...conv.messages, msg],
                };
              } else {
                next.push(conv);
              }
            }

            return updatedConv ? [updatedConv, ...next] : prev;
          });

          setSelectedChat((prev) =>
            prev?.id === msg.conversationId
              ? {
                  ...prev,
                  messageCount: Math.max(prev.messageCount, messageCount),
                  lastMessageAt,
                  _count: {
                    ...prev._count,
                    messages: unreadCount,
                  },
                  messages: prev.messages.some(
                    (message) =>
                      message.messageId === msg.messageId ||
                      (msg.providerMessageKey &&
                        message.providerMessageKey === msg.providerMessageKey),
                  )
                    ? prev.messages
                    : [...prev.messages, msg],
                }
              : prev,
          );

          handleIncomingNotification(msg);

          return;
        }
      }

      default:
        return;
    }
  }, [
    fetchConversations,
    handleIncomingNotification,
    handleMessageSchedulingUpdate,
    handleTranscriptUpdate,
    messages,
    setConversations,
    user?.id,
  ]);

  useEffect(() => {
    if (hasActiveFilters(debouncedFilters)) {
      // Sem o termo: com outros filtros ativos, mandá-lo junto reintroduziria o
      // ILIKE sobre mensagens que este trabalho justamente tirou do caminho.
      fetchFilteredConversations({ ...debouncedFilters, search: "" });
    } else {
      const cursorDate = new Date();
      cursorDate.setHours(0, 0, 0, 0);
      cursorDate.setDate(cursorDate.getDate() + 1);

      fetchConversations(cursorDate);
    }
  }, [
    debouncedFilters,
    fetchConversations,
    fetchFilteredConversations,
  ]);

  const sendMessage = useCallback(async () => {
    if (!payload.text.trim() && !payload.attachment) return;
    if (!selectedChat) return;
    const normalizedText = normalizeText(payload.text);

    if (!normalizedText && !payload.attachment) return;

    setSendingMessage(true);

    const hasText = payload.text.trim().length > 0;
    const message = hasText
      ? payload.text.replace(/\r\n/g, "\n")
      : "Envio de Anexo";

    try {
      const finalPayload = {
        conversationId: selectedChat.id,
        userId: user.id || null,
        username: user.name || null,
        phoneNumber: selectedChat.contact.phoneNumber,
        message: message,
        quoteMessageId: payload?.quoteMessageId || null,
        attachment: payload?.attachment || null,
        scheduleAt: payload?.scheduleAt || null,
      };

      const success = sendAction("send_message", finalPayload);
      if (!success) throw new Error("Falha ao enviar mensagem.");

      setPayload({
        text: "",
        attachment: null,
        scheduleAt: "",
        quoteMessageId: null,
      });
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      toast.error("Erro ao enviar mensagem.");
    } finally {
      setSendingMessage(false);
    }
  }, [payload, selectedChat, sendAction, user.id, user.name]);

  const sendAudioMessage = async () => {
    if (!audioBlob || !selectedChat) return;

    setSendingMessage(true);

    try {
      const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, {
        type: "audio/webm;codecs=opus",
      });

      // Fazer upload do áudio como anexo
      const formData = new FormData();
      formData.append("name", audioFile.name);
      formData.append("userIds", user.id);
      formData.append("hidden", true);
      formData.append("file", audioFile);

      const attach = await addAttachment(formData);

      // Enviar mensagem com o áudio
      const finalPayload = {
        conversationId: selectedChat.id,
        userId: user.id || null,
        username: user.name || null,
        phoneNumber: selectedChat.contact.phoneNumber,
        message: "audio",
        quoteMessageId: payload?.quoteMessageId || null,
        attachment: attach.filePath,
        scheduleAt: payload?.scheduleAt || null,
      };

      const success = sendAction("send_message", finalPayload);
      if (!success) throw new Error("Falha ao enviar áudio.");

      cancelRecording();

      if (payload?.scheduleAt) {
        toast.success(
          "Áudio agendado para " +
            new Date(payload.scheduleAt).toLocaleString("pt-BR") +
            "!",
        );
      } else {
        toast.success("Áudio enviado!");
      }
    } catch (err) {
      console.error("Erro ao enviar áudio:", err);
      toast.error("Erro ao enviar áudio.");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  const handleDropzoneUpload = async (file) => {
    try {
      const formData = new FormData();

      formData.append("name", file.name);
      formData.append("userIds", user.id);
      formData.append("file", file);

      const attach = await addAttachment(formData);

      setPayload((prev) => ({
        ...prev,
        attachment: attach.filePath,
      }));

      setActivePanel(null);
    } catch (err) {
      console.error("Erro ao enviar anexo:", err);
    }
  };

  const handleSelectQuickMessage = (qm) => {
    setPayload((prev) => ({
      ...prev,
      text: qm.content,
      attachment: qm.attachmentUrl || null,
    }));

    setActivePanel(null);
  };

  const handleSelectExistingAttachment = useCallback((att) => {
    setPayload((prev) => ({
      ...prev,
      attachment: att.filePath,
    }));

    setActivePanel(null);
  }, []);

  const handleSelectQuoteMessage = useCallback((messageId) => {
    setPayload((prev) => ({
      ...prev,
      quoteMessageId: messageId,
    }));
  }, []);

  const handleOpenForwardModal = useCallback((message) => {
    setForwardMessage(message);
    setOpenForwardMessageModal(true);
  }, []);

  const handleCloseForwardModal = useCallback(() => {
    setForwardMessage(null);
    setOpenForwardMessageModal(false);
  }, []);

  const handleForwardMessage = useCallback(
    (contacts) => {
      if (!forwardMessage) return;

      sendAction("forward_message", {
        messageId: forwardMessage,
        contacts,
      });

      handleCloseForwardModal();
    },
    [forwardMessage, sendAction, handleCloseForwardModal],
  );

  const handleSelectEmote = (emoji) => {
    setPayload((prev) => ({
      ...prev,
      text: prev.text + emoji,
    }));
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;

    for (const item of items) {
      if (item.kind === "file") {
        const file = item.getAsFile();

        if (file) {
          handleDropzoneUpload(file);
          e.preventDefault();
          break;
        }
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;

    if (!files || files.length === 0) return;

    const file = files[0];
    handleDropzoneUpload(file);
  };

  const markAsRead = async () => {
    try {
      const id = selectedChat.id;

      const conversation = await api.markAsRead(id);

      setConversations((prev) =>
        prev.map((c) => (c.id === id ? conversation : c)),
      );

      setSelectedChat((prev) => {
        if (!prev) return prev;
        if (prev.id !== id) return prev;

        return conversation;
      });
    } catch (error) {
      toast.error(error);
      console.error("Erro ao atualizar status:", error);
    }
  };

  const { unreadConversationsCount, archivedUnreadCount } =
    useMemo(() => {
      return conversations.reduce(
        (acc, conv) => {
          const hasUnread = conv._count.messages > 0;

          if (hasUnread) {
            acc.unreadConversationsCount += 1;
            if (conv.status === "ARQUIVADO") {
              acc.archivedUnreadCount += 1;
            }
          }

          return acc;
        },
        {
          unreadConversationsCount: 0,
          archivedUnreadCount: 0,
        },
      );
    }, [conversations]);

  const quotedMessage = payload.quoteMessageId
    ? selectedChat?.messages?.find(
        (m) => m.messageId === payload.quoteMessageId,
      )
    : null;

  useEffect(() => {
    if (unreadConversationsCount > 0) {
      document.title = `ChatJuri (${unreadConversationsCount})`;
    } else {
      document.title = "ChatJuri";
    }
  }, [unreadConversationsCount]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-red-500">Erro: {error}</p>
          <button
            onClick={() => {
              // Sem a arrow, o onClick entrega o evento de clique como cursor de
              // data e a nova tentativa falha com "Invalid Date" no Prisma.
              const cursorDate = new Date();
              cursorDate.setHours(0, 0, 0, 0);
              cursorDate.setDate(cursorDate.getDate() + 1);
              fetchConversations(cursorDate);
            }}
            className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Lista de Conversas */}
      <div className="flex h-full w-80 flex-col border-r border-gray-200 bg-white transition-all duration-300 dark:border-gray-700 dark:bg-gray-800">
        {/* Header */}
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              Conversas
            </h2>

            <div className="flex space-x-2">
              <button
                onClick={() =>
                  updateFilters(
                    "status",
                    filters.status === "ARQUIVADO" ? "" : "ARQUIVADO",
                  )
                }
                className="relative rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Abrir filtros"
                title="Mensagens Arquivadas"
              >
                <ArchiveRestore className="h-5 w-5 text-gray-600 dark:text-gray-300" />

                {archivedUnreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                    {archivedUnreadCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Abrir filtros"
                title="Filtros"
              >
                <Filter className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>

              <button
                onClick={() => setOpenDirectMessageModal(true)}
                className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Abrir contatos"
                title="Contatos"
              >
                <MessageCirclePlus className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Busca */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-300" />

            <input
              type="text"
              placeholder="Buscar conversas..."
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 placeholder-gray-400 outline-none transition-colors duration-200 focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              value={filters.search}
              onChange={(e) => updateFilters("search", e.target.value)}
            />
          </div>

          {/* Filtros */}
          {showFilters && (
            <FiltersList
              users={users}
              tags={tags}
              funnels={funnels}
              filters={filters}
              onChange={updateFilters}
            />
          )}

          {/* Estatísticas Rápidas */}
          <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3 transition-colors dark:bg-gray-900/50">
            <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Mostrar apenas não lidas
            </label>
            <button
              onClick={() => updateFilters("unread", !filters.unread)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                filters.unread ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"
              }`}
              role="switch"
              aria-checked={filters.unread}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${
                  filters.unread ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        {searching ? (
          <SearchResults
            result={searchResult}
            loading={searchLoading}
            onOpen={openFromSearch}
          />
        ) : loading ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            <LoaderCircle className="mx-auto mb-2 h-8 w-8 animate-spin text-gray-300 dark:text-gray-600" />
            <p className="text-sm">Carregando Conversas</p>
          </div>
        ) : (
          <ConversationsList
            conversations={conversations}
            pinnedIds={pinnedIds}
            selectedChat={selectedChat}
            setSelectedChat={setSelectedChat}
            onLoadMore={fetchMoreConversations}
          />
        )}
      </div>

      {/* Área de Chat */}
      {!selectedChat ? (
        <div className="flex flex-1 items-center justify-center bg-gray-100 dark:bg-gray-900">
          <div className="text-center">
            <MessageSquare className="mx-auto mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
            <p className="text-lg text-gray-500 dark:text-gray-400">
              Selecione uma conversa para começar
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col bg-white dark:bg-gray-800">
          {/* Header do Chat */}
          <ChatHeader
            selectedChat={selectedChat}
            pinnedIds={pinnedIds}
            togglePinConversation={togglePinConversation}
            markAsRead={markAsRead}
            setShowContactInfo={setShowContactInfo}
          />

          <ChatMessages
            messages={selectedChat.messages}
            contact={selectedChat.contact}
            conversationId={selectedChat.id}
            sendAction={sendAction}
            onReplyMessage={handleSelectQuoteMessage}
            onForwardMessage={handleOpenForwardModal}
          />

          {/* Input de Mensagem */}
          <div className="space-y-3 border-t border-gray-200 p-4 dark:border-gray-700">
            {/* Seção de mensagens rápidas */}
            {activePanel === "quickMessages" && (
              <QuickMessageBlock
                quickMessages={quickMessages}
                onSendQuickMessage={handleSelectQuickMessage}
              />
            )}

            {/* Seção de anexos */}
            {activePanel === "attachments" && (
              <div className="mb-3 space-y-3">
                {/* Upload de arquivo */}
                <Dropzone onFileUpload={handleDropzoneUpload} />

                {/* Lista de anexos */}
                <AttachmentList
                  attachments={attachments}
                  onSendAttachment={handleSelectExistingAttachment}
                />
              </div>
            )}

            {/* Seção de agendamento */}
            {activePanel === "schedule" && (
              <div className="mb-3 space-y-3">
                <div className="w-full rounded-lg border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Data e Hora
                  </label>
                  <input
                    type="datetime-local"
                    value={payload.scheduleAt}
                    onChange={(e) =>
                      setPayload((prev) => ({
                        ...prev,
                        scheduleAt: e.target.value,
                      }))
                    }
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full rounded-lg bg-white px-3 py-2 text-gray-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>

                <ScheduledMessagesList
                  scheduledMessages={selectedChat?.scheduledMessages || []}
                  onCancel={cancelScheduledMessage}
                />
              </div>
            )}

            {/* Seção de emotes */}
            {activePanel === "emotes" && (
              <div className="mb-3">
                <EmotesPanel onSelect={handleSelectEmote} />
              </div>
            )}

            {/* Seção de mensagem referenciada */}
            {quotedMessage && (
              <div className="mb-3">
                <div className="flex items-start gap-3 rounded-lg border-l-4 border-green-500 bg-gray-50 p-3 dark:bg-gray-900">
                  <div className="flex-1 overflow-hidden">
                    <p className="text-xs font-semibold text-green-600 dark:text-green-400">
                      Respondendo a{" "}
                      {quotedMessage.direction === "OUTGOING"
                        ? "você"
                        : selectedChat.contact.name}
                    </p>

                    <p className="truncate text-sm text-gray-700 dark:text-gray-300">
                      {quotedMessage.messageType === "text" &&
                        quotedMessage.messageBody}

                      {quotedMessage.messageType === "document" &&
                        "📎 Documento enviado"}

                      {quotedMessage.messageType === "audio" &&
                        "🎤 Mensagem de áudio"}
                    </p>
                  </div>

                  <button
                    onClick={() =>
                      setPayload((prev) => ({ ...prev, quoteMessageId: null }))
                    }
                    className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-red-500 dark:hover:bg-gray-700"
                    title="Remover resposta"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* Indicador de gravação */}
            {isRecording && (
              <div className="flex items-center justify-center space-x-2 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                <div className="h-3 w-3 animate-pulse rounded-full bg-red-500"></div>
                <span className="text-sm font-medium text-red-600 dark:text-red-400">
                  Gravando... {formatRecordingTime(recordingTime)}
                </span>
              </div>
            )}

            {/* Indicativo de arquivo anexado */}
            {payload.attachment &&
              !audioUrl &&
              (() => {
                const fileUrl = getFileUrl(payload.attachment);
                const fileType = getFileType(payload.attachment);

                return (
                  <div className="flex flex-col gap-2 rounded-lg border border-green-300 bg-green-100 p-3 text-sm text-green-800 dark:border-green-700 dark:bg-green-900 dark:text-green-200">
                    {/* Preview */}
                    <div className="w-full">
                      {fileType === "image" && (
                        <img
                          src={fileUrl}
                          alt="preview"
                          className="max-w-64 rounded-lg object-cover"
                        />
                      )}

                      {fileType === "video" && (
                        <video controls className="max-h-48 w-full rounded-lg">
                          <source src={fileUrl} />
                        </video>
                      )}

                      {fileType === "audio" && (
                        <audio controls className="mb-2 w-full">
                          <source src={fileUrl} />
                        </audio>
                      )}

                      {fileType === "doc" && (
                        <iframe
                          src={fileUrl}
                          className="h-48 w-full rounded-lg"
                          title="Preview de documento"
                        />
                      )}

                      {fileType === "other" && (
                        <div className="flex items-center gap-2">
                          <span>Arquivo não suportado para preview</span>
                        </div>
                      )}
                    </div>

                    {/* Info + ações */}
                    <div className="flex items-center gap-2">
                      <Paperclip />

                      <div className="mr-auto flex flex-col overflow-hidden leading-tight">
                        <span className="font-medium">Arquivo anexado</span>
                        <span className="max-w-xs truncate text-xs opacity-80">
                          {payload.attachment.split("/").pop()}
                        </span>
                      </div>

                      {/* Editar */}
                      <button
                        onClick={() =>
                          setActivePanel(
                            activePanel === "attachments"
                              ? null
                              : "attachments",
                          )
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/60 dark:text-blue-400"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>

                      {/* Remover */}
                      <button
                        onClick={() =>
                          setPayload((prev) => ({ ...prev, attachment: null }))
                        }
                        className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/60 dark:text-red-400"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                );
              })()}

            {/* Indicativo de agendamento */}
            {payload.scheduleAt && (
              <div className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-100 px-3 py-2 text-sm text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900 dark:text-yellow-200">
                <Calendar />

                <div className="mr-auto flex flex-col leading-tight">
                  <span className="font-medium">Mensagem agendada</span>
                  <span className="text-xs opacity-80">
                    {new Date(payload.scheduleAt).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>

                {/* Editar */}
                <button
                  onClick={() =>
                    setActivePanel(
                      activePanel === "schedule" ? null : "schedule",
                    )
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/60 dark:text-blue-400 dark:hover:bg-blue-900"
                >
                  <Pencil className="h-5 w-5" />
                </button>

                {/* Remover */}
                <button
                  onClick={() =>
                    setPayload((prev) => ({ ...prev, scheduleAt: "" }))
                  }
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 text-red-600 transition-colors hover:bg-red-200 dark:bg-red-900/60 dark:text-red-400 dark:hover:bg-red-900"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            )}

            {/* Linha de envio */}
            {!audioUrl ? (
              <div className="flex items-center space-x-2">
                <div className="grid grid-cols-2 gap-2">
                  {inputButtons.map(({ key, title, Icon }) => (
                    <button
                      key={key}
                      onClick={() => {
                        setActivePanel(activePanel === key ? null : key);
                      }}
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                      title={title}
                    >
                      <Icon className="h-5 w-5" />
                    </button>
                  ))}
                </div>

                <textarea
                  ref={messageInputRef}
                  placeholder="Digite uma mensagem..."
                  className="flex-1 rounded-lg bg-gray-100 px-4 py-2 text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-100"
                  value={payload.text}
                  onPaste={handlePaste}
                  onDrop={handleDrop}
                  onChange={(e) =>
                    setPayload((prev) => ({ ...prev, text: e.target.value }))
                  }
                  onKeyDown={handleKeyDown}
                  disabled={sendingMessage || isRecording}
                  rows={3}
                ></textarea>

                {!isRecording ? (
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={startRecording}
                      className="rounded-lg bg-blue-500 p-2 text-white transition-colors hover:bg-blue-600"
                      title="Gravar áudio"
                      disabled={sendingMessage}
                    >
                      <Mic className="h-5 w-5" />
                    </button>

                    <button
                      onClick={sendMessage}
                      className="rounded-lg bg-green-500 p-2 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={
                        (!payload.text.trim() && !payload.attachment) ||
                        sendingMessage ||
                        isRecording
                      }
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={stopRecording}
                    className="rounded-lg bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                    title="Parar gravação"
                  >
                    <StopCircle className="h-5 w-5" />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3 rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                <div className="flex-1">
                  <audio controls className="w-full">
                    <source src={audioUrl} type="audio/webm" />
                    Seu navegador não suporta o player de áudio.
                  </audio>
                </div>
                <button
                  onClick={cancelRecording}
                  className="rounded-lg bg-red-500 p-2 text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Cancelar gravação"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
                <button
                  onClick={sendAudioMessage}
                  className="rounded-lg bg-green-500 p-2 text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={sendingMessage}
                  title="Enviar áudio"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Painel Lateral de Informações */}
      {showContactInfo && (
        <ClientSidebar
          api={api}
          user={user}
          toast={toast}
          selectedChat={selectedChat}
          setConversations={setConversations}
          setSelectedChat={setSelectedChat}
          summarizeConversation={summarizeConversation}
          deleteConversation={deleteConversation}
          users={users}
          funnels={funnels}
          tags={tags}
        />
      )}

      {openDirectMessageModal && (
        <DirectMessageList
          onClose={() => setOpenDirectMessageModal(false)}
          contacts={contacts}
          user={user}
          sendAction={sendAction}
          setConversations={setConversations}
          setSelectedChat={setSelectedChat}
          toast={toast}
          api={api}
        />
      )}

      {openForwardMessageModal && (
        <ForwardMessageModal
          onClose={handleCloseForwardModal}
          contacts={contacts}
          onForward={handleForwardMessage}
        />
      )}
    </>
  );
};

export default Conversations;
